import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Form, { Validations } from '../../../../../components/Form';
import useRequest from '../../../../../hooks/useRequest';
import { ControlItem } from '../../../types';
import { checkIsRequired, formatParam } from '../../helpers';

import ServicePaths from '../../../../../bridge/utils/ServicePaths';

const MoveBalanceForm = () => {
  const [userId, setUserId] = useState<string | undefined>();
  const [botIdFrom, setBotIdFrom] = useState<string | undefined>();
  const [botIdTo, setBotIdTo] = useState<string | undefined>();
  const [balance, setBalance] = useState<string | undefined>();
  const [balanceType, setBalanceType] = useState<string | undefined>();
  const [destinationBalanceType, setDestinationBalanceType] = useState<
    string | undefined
  >();
  const [useMultiOrders, setUseMultiOrders] = useState<string | undefined>();

  const [errors, setErrors] = useState<Record<string, Validations>>({});
  const [canFetchBalance, setCanFetchBalance] = useState(false);
  const [formMessage, setFormMessage] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const params = useMemo(
    () => ({ userId, botId: botIdFrom }),
    [userId, botIdFrom],
  );

  const options = useMemo(
    () => ({ shouldFetch: canFetchBalance }),
    [canFetchBalance],
  );

  const { data: balanceData } = useRequest(
    balanceType === 'stable'
      ? ServicePaths.CONTROL.GetStableBalance
      : ServicePaths.CONTROL.GetAssetBalance,
    params,
    options,
  );

  useEffect(() => {
    if (balanceData) {
      setIsLoading(false);
    }
  }, [balanceData]);

  const checkIdsValidation = useCallback(() => {
    /** userId can not be empty */
    let didError;

    setErrors({});

    didError = checkIsRequired(userId, setErrors, 'userId') || didError;
    didError = checkIsRequired(botIdFrom, setErrors, 'botIdFrom') || didError;

    if (typeof balanceType === 'undefined' || balanceType?.length === 0) {
      setErrors((prev) => ({
        ...prev,
        balanceType: Validations.Required,
      }));

      didError = true;
    } else if (balanceType !== 'asset' && balanceType !== 'stable') {
      setErrors((prev) => ({
        ...prev,
        balanceType: Validations.BalanceTypeTyping,
      }));

      didError = true;
    }

    return !didError;
  }, [balanceType, botIdFrom, userId]);

  const handleGetAvailableBalanceSubmit = useCallback(() => {
    setIsLoading(true);
    const canContinue = checkIdsValidation();

    if (!canContinue) {
      setIsLoading(false);
      return;
    }

    setCanFetchBalance(true);
  }, [checkIdsValidation]);

  const checkBalanceValidation = useCallback(() => {
    /** userId can not be empty */
    let didError;

    setErrors({});

    if (typeof balance === 'undefined' || balance?.length === 0) {
      setErrors((prev) => ({
        ...prev,
        balance: Validations.Required,
      }));

      didError = true;
    } else if (typeof balance !== 'undefined') {
      const balanceValue = parseFloat(balance);

      if (isNaN(balanceValue)) {
        setErrors((prev) => ({
          ...prev,
          balance: Validations.NumberTyping,
        }));

        didError = true;
      }
    }

    if (balanceType === 'asset') {
      if (
        typeof destinationBalanceType === 'undefined' ||
        destinationBalanceType?.length === 0
      ) {
        setErrors((prev) => ({
          ...prev,
          destinationBalanceType: Validations.Required,
        }));

        didError = true;
      } else if (
        destinationBalanceType !== 'asset' &&
        destinationBalanceType !== 'stable'
      ) {
        setErrors((prev) => ({
          ...prev,
          destinationBalanceType: Validations.BalanceTypeTyping,
        }));

        didError = true;
      }
    }

    if (useMultiOrders !== 'true' && useMultiOrders !== 'false') {
      setErrors((prev) => ({
        ...prev,
        useMultiOrders: Validations.BooleanTyping,
      }));

      didError = true;
    }

    return !didError;
  }, [balance, balanceType, destinationBalanceType, useMultiOrders]);

  const handleMoveBalanceSubmit = useCallback(async () => {
    setIsLoading(true);
    const canContinue = checkBalanceValidation();

    if (!canContinue) {
      setIsLoading(false);
      return;
    }

    const { data } = await axios({
      method: 'POST',
      url:
        balanceType === 'stable'
          ? ServicePaths.CONTROL.MoveStableBalance
          : ServicePaths.CONTROL.MoveAssetBalance,
      params: {
        userId: formatParam(userId),
        botIdFrom: formatParam(botIdFrom),
        botIdTo: formatParam(botIdTo),
        balance: formatParam(balance),
        destinationBalanceType: formatParam(destinationBalanceType),
        useMultiOrders: formatParam(useMultiOrders),
      },
    });

    setIsLoading(false);
    setFormMessage(data);
  }, [
    balance,
    balanceType,
    botIdFrom,
    botIdTo,
    checkBalanceValidation,
    destinationBalanceType,
    useMultiOrders,
    userId,
  ]);

  const getAvailableBalanceConfig = useMemo(() => {
    return {
      title: ControlItem.MoveBalance,
      fields: [
        {
          label: 'User Id',
          value: userId,
          handleValueChange: setUserId,
          error: errors.userId,
        },
        {
          label: 'From Bot Id',
          value: botIdFrom,
          handleValueChange: setBotIdFrom,
          error: errors.botIdFrom,
        },
        {
          label: 'Balance Type',
          value: balanceType,
          handleValueChange: setBalanceType,
          error: errors.balanceType,
        },
      ],
      handleSubmit: handleGetAvailableBalanceSubmit,
      isLoading,
    };
  }, [
    balanceType,
    botIdFrom,
    errors.balanceType,
    errors.botIdFrom,
    errors.userId,
    handleGetAvailableBalanceSubmit,
    isLoading,
    userId,
  ]);

  const moveBalanceConfig = useMemo(() => {
    return {
      title: ControlItem.MoveBalance,
      formMessage: formMessage ?? balanceData,
      fields: [
        {
          label: 'User Id',
          value: userId,
          handleValueChange: setUserId,
          error: errors.userId,
          isLocked: true,
        },
        {
          label: 'From Bot Id',
          value: botIdFrom,
          handleValueChange: setBotIdFrom,
          error: errors.botIdFrom,
          isLocked: true,
        },
        {
          label: 'To Bot Id',
          value: botIdTo,
          handleValueChange: setBotIdTo,
          error: errors.botIdTo,
        },
        {
          label: 'Balance Type',
          value: balanceType,
          handleValueChange: setBalanceType,
          error: errors.balanceType,
          isLocked: true,
        },
        {
          label: 'Destination Balance Type',
          value: destinationBalanceType,
          handleValueChange: setDestinationBalanceType,
          error: errors.destinationBalanceType,
          isLocked: balanceType === 'stable',
        },
        {
          label: 'Balance',
          value: balance,
          handleValueChange: setBalance,
          error: errors.balance,
        },
        {
          label: 'Use Multi Orders',
          value: useMultiOrders,
          handleValueChange: setUseMultiOrders,
          error: errors.useMultiOrders,
        },
      ],
      handleSubmit: handleMoveBalanceSubmit,
      isLoading,
    };
  }, [
    balance,
    balanceData,
    balanceType,
    botIdFrom,
    botIdTo,
    destinationBalanceType,
    errors.balance,
    errors.balanceType,
    errors.botIdFrom,
    errors.botIdTo,
    errors.destinationBalanceType,
    errors.useMultiOrders,
    errors.userId,
    formMessage,
    handleMoveBalanceSubmit,
    isLoading,
    useMultiOrders,
    userId,
  ]);

  if (!balanceData) {
    return <Form config={getAvailableBalanceConfig} />;
  }

  return <Form config={moveBalanceConfig} />;
};

export default MoveBalanceForm;
